/**
 * Мониторинг (Remetry) — web-вариант.
 * Общая часть (конфиг, состояние, сборка и отправка событий) — в
 * monitoring.shared.ts, здесь только своё: плагин и init.
 *
 * performancePlugin — штатный, берём как есть. errorsPlugin заменяем своим:
 * штатный шлёт события через captureError, который не проставляет userId.
 * apiPlugin не подключаем: он шлёт type:'api' на каждый запрос без userId и
 * без фильтра 401/403, а это дублирует reportApiCall из api/client.ts —
 * единственный источник api-событий на обеих платформах. Цена решения:
 * запросы мимо api/client.ts (Keycloak/OIDC-флоу, статика/ассеты) в
 * api-события не попадают.
 * replayPlugin (rrweb) не включаем — портал корпоративный, приватность.
 *
 * Fail-safe: тело каждой экспортируемой функции и каждого обработчика обёрнуто
 * в try/catch — мониторинг никогда не должен ронять приложение.
 */
import { init, flush, performancePlugin, type Plugin } from '@remetry/browser'
import { armMonitoring, dsn, endpoint, release, sendError } from './monitoring.shared'

function webErrorsPlugin(): Plugin {
  return {
    name: 'web-errors',
    setup() {
      if (typeof window === 'undefined') return

      window.addEventListener('error', (e) => {
        try {
          sendError(e.error ?? e.message)
        } catch {
          // см. шапку модуля
        }
      })
      window.addEventListener('unhandledrejection', (e) => {
        try {
          sendError(e.reason)
        } catch {
          // см. шапку модуля
        }
      })
      // вкладку закрывают — последний шанс отправить очередь
      window.addEventListener('pagehide', () => {
        try {
          flush(true)
        } catch {
          // см. шапку модуля
        }
      })
    },
  }
}

/**
 * Адрес для стампа события — БЕЗ query. Стамп едет наружу, в сторонний мониторинг, на каждом
 * событии, а в адресе бывает секрет: одноразовый токен восстановления пароля
 * (`/reset-password?token=…`) — ровно та ссылка, по которой человек приходит из письма.
 * Путь для разбора ошибок достаточен, а параметры — лишний способ утечь.
 */
export function stampUrl(href?: string): string | undefined {
  return href?.split(/[?#]/)[0];
}

/** Идемпотентно. Пустой dsn/endpoint — kill-switch: SDK не инициализируется вовсе. */
export function initMonitoring(): void {
  try {
    // Платформенный стамп: url/browser в каждом событии. Guard-ы на
    // location/navigator — ради статического рендера web-сборки.
    const armed = armMonitoring((event) => ({
      url: stampUrl(event.url ?? (typeof location !== 'undefined' ? location.href : undefined)),
      browser: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    }))
    if (!armed) return
    init({
      dsn,
      endpoint,
      release,
      integrations: [webErrorsPlugin(), performancePlugin()],
    })
  } catch {
    // см. шапку модуля
  }
}

export { setMonitoringUser, reportError, reportForbidden, reportApiCall } from './monitoring.shared'
